import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { DangerousHtml } from '~components/DangerousHtml';
import ChevronLeft from '~public/assets/icons/chevron-left.svg';
import ChevronRight from '~public/assets/icons/chevron-right.svg';
import Close from '~public/assets/icons/close.svg';

import type { RetentionBanner } from '../../model/types';
import { PageIndicator } from './PageIndicator';
import {
	BannerSlidesContainer,
	ButtonsContainer,
	CloseButton,
	PageIndicatorContainer,
	SliderButton,
} from './styles';

type Props = {
	banners: RetentionBanner[];
	onClose?: (bannerId: string) => void;
	scrollEnabled?: boolean;
};

const Component = ({ banners, onClose, scrollEnabled }: Props) => {
	const interval = useRef<ReturnType<typeof setTimeout>>(null);

	const [currentContentIndex, setCurrentContextIndex] = useState(0);

	const canAutoScrollBanners = scrollEnabled && banners.length > 0;

	const startAutoscroll = useCallback(() => {
		if (banners.length > 1) {
			interval.current = setInterval(() => {
				setCurrentContextIndex((index) => (index + 1) % banners.length);
			}, 3_000);
		} else {
			setCurrentContextIndex(0);
		}
	}, [banners]);

	const stopAutoscroll = useCallback(() => {
		interval.current && clearInterval(interval.current);
	}, []);

	const restartAutoscroll = useCallback(() => {
		stopAutoscroll();
		startAutoscroll();
	}, [stopAutoscroll, startAutoscroll]);

	const showNextBanner = () => {
		if (canAutoScrollBanners) {
			setCurrentContextIndex((index) => (index + 1) % banners.length);
			restartAutoscroll();
		}
	};

	const showPreviousBanner = () => {
		if (canAutoScrollBanners) {
			setCurrentContextIndex(
				(index) => (index - 1 + banners.length) % banners.length
			);
			restartAutoscroll();
		}
	};

	useEffect(() => {
		scrollEnabled && startAutoscroll();

		return () => {
			stopAutoscroll();
		};
	}, [startAutoscroll, stopAutoscroll, scrollEnabled]);

	if (!banners.length) {
		return null;
	}

	return (
		<>
			{onClose && (
				<CloseButton onClick={() => onClose(banners[currentContentIndex].id)}>
					<Close />
				</CloseButton>
			)}
			{scrollEnabled && banners.length > 1 && (
				<BannerSlidesContainer>
					<PageIndicatorContainer>
						{banners.map((_: unknown, index: number) => {
							return (
								<PageIndicator
									key={index}
									active={index === currentContentIndex}
								/>
							);
						})}
					</PageIndicatorContainer>
					<ButtonsContainer>
						<SliderButton onClick={showPreviousBanner}>
							<ChevronLeft />
						</SliderButton>
						<SliderButton onClick={showNextBanner}>
							<ChevronRight />
						</SliderButton>
					</ButtonsContainer>
				</BannerSlidesContainer>
			)}
			<DangerousHtml>{banners[currentContentIndex].content}</DangerousHtml>
		</>
	);
};

export const AbstractBanner = memo(Component);
